import type { InterviewType, Meeting } from "@scoach/types";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { api } from "../../lib/http.ts";
import { isProdEnv } from "../../lib/runtimeConfig.ts";
import { DashHeader } from "../dashboard/components/DashHeader.tsx";
import { interviewApi } from "./api.ts";

type Tab = "all" | "completed" | "drafts";

const STATUS_LABEL: Record<string, string> = {
  draft: "Not started",
  live: "In progress",
  ended: "Evaluating",
  summarized: "Completed",
};
const STATUS_COLOR: Record<string, string> = {
  draft: "var(--text-3)",
  live: "#f9ab00",
  ended: "#1a73e8",
  summarized: "#34a853",
};

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function InterviewListScreen() {
  const nav = useNavigate();
  const [items, setItems] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [typeNames, setTypeNames] = useState<Record<string, string>>({});

  useEffect(() => {
    api<{ items: Meeting[] }>("/meetings")
      .then((r) => {
        const interviews = (r.items ?? []).filter((m) => m.meetingType === "interview");
        interviews.sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
        setItems(interviews);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Map interview-type id → display name for richer list rows.
    interviewApi
      .fetchTypes()
      .then((types: InterviewType[]) =>
        setTypeNames(Object.fromEntries(types.map((t) => [t.id, t.name]))),
      )
      .catch(() => {});
  }, []);

  const candidateName = (m: Meeting): string => (m.interview?.candidateName ?? "").trim();
  const typeName = (m: Meeting): string => {
    const id = m.interview?.interviewTypeId;
    if (!id) return "";
    return typeNames[id] ?? id.charAt(0).toUpperCase() + id.slice(1);
  };

  async function newInterview() {
    setCreating(true);
    try {
      const m = await interviewApi.create({
        account: { name: "Interview" },
        title: "Interview",
        stage: "Intro",
      });
      nav({ to: "/interviews/$id/setup", params: { id: m.id } });
    } catch (err) {
      alert(`Could not create interview: ${(err as Error).message}`);
      setCreating(false);
    }
  }

  function open(m: Meeting) {
    const to =
      m.status === "summarized" || m.status === "ended"
        ? "/interviews/$id/summary"
        : "/interviews/$id/setup";
    nav({ to, params: { id: m.id } });
  }

  const filtered = useMemo(() => {
    let list = items;
    if (tab === "completed")
      list = list.filter((m) => m.status === "summarized" || m.status === "ended");
    else if (tab === "drafts")
      list = list.filter((m) => m.status === "draft" || m.status === "live");
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((m) => {
        const iv = m.interview;
        return [m.interview?.candidateName, m.title, iv?.roleType, iv?.candidateLevel, typeName(m)]
          .filter(Boolean)
          .some((s) => (s as string).toLowerCase().includes(q));
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tab, search, typeNames]);

  const completed = items.filter((m) => m.status === "summarized").length;

  return (
    <div className="dash">
      <DashHeader onStartNew={newInterview} startLabel="New Interview" />

      <div className="dash-grid">
        <section className="dash-hero">
          <div className="dash-hero-text">
            <h1 className="dash-title">
              Interviews{" "}
              {!isProdEnv() && (
                <span className="brand-tag" style={{ background: "#f9ab00", color: "#000" }}>
                  QC
                </span>
              )}
            </h1>
            <p className="dash-sub">
              Run a structured, AI-assisted interview and get an auto-generated evaluation
              scorecard.
            </p>
            <div className="dash-cta-row">
              <button
                type="button"
                className="pill-btn primary"
                onClick={newInterview}
                disabled={creating}
              >
                {creating ? "Creating…" : "New interview"}
              </button>
            </div>
          </div>
          <div className="dash-hero-stats">
            <div className="stat-tile">
              <div className="stat-value">{items.length}</div>
              <div className="stat-label">Total interviews</div>
            </div>
            <div className="stat-tile">
              <div className="stat-value">{completed}</div>
              <div className="stat-label">Completed</div>
            </div>
          </div>
        </section>

        <section className="dash-history">
          <div className="dash-history-head">
            <h2 className="dash-h2">Interview history</h2>
            <div
              className="dash-history-actions"
              style={{ display: "flex", gap: 8, alignItems: "center" }}
            >
              <input
                type="search"
                placeholder="Search candidate, role, type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid var(--border-1)",
                  background: "var(--surface-2)",
                  color: "var(--text-1)",
                  fontSize: 13,
                  minWidth: 200,
                }}
              />
              <div className="seg seg-tabs">
                <button
                  type="button"
                  className={tab === "all" ? "on" : ""}
                  onClick={() => setTab("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={tab === "completed" ? "on" : ""}
                  onClick={() => setTab("completed")}
                >
                  Completed
                </button>
                <button
                  type="button"
                  className={tab === "drafts" ? "on" : ""}
                  onClick={() => setTab("drafts")}
                >
                  In progress
                </button>
              </div>
            </div>
          </div>

          {loading && <p style={{ color: "var(--text-3)", padding: "12px 4px" }}>Loading…</p>}
          {!loading && filtered.length === 0 && (
            <p style={{ color: "var(--text-3)", padding: "12px 4px" }}>
              No interviews yet. Click <strong>New interview</strong> to start one.
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
            {filtered.map((m) => {
              const iv = m.interview;
              const status = m.status ?? "draft";
              return (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => open(m)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "110px 1fr auto",
                    alignItems: "center",
                    gap: 16,
                    textAlign: "left",
                    background: "var(--surface)",
                    border: "1px solid var(--border-1)",
                    borderRadius: 12,
                    padding: "14px 18px",
                    boxShadow: "var(--sh-1)",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{ fontSize: 12, color: "var(--text-3)", fontFamily: "var(--font-mono)" }}
                  >
                    {fmtDate(m.updatedAt ?? m.createdAt)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {candidateName(m) || m.title || "Interview"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 1 }}>
                      {[typeName(m), iv?.roleType, iv?.candidateLevel]
                        .filter(Boolean)
                        .join(" · ") || "Not configured yet"}
                    </div>
                    {iv?.isManager && (
                      <div style={{ fontSize: 11, color: "#7627bb", marginTop: 2 }}>
                        Manager / leadership track
                      </div>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".04em",
                      color: STATUS_COLOR[status] ?? "var(--text-3)",
                    }}
                  >
                    {STATUS_LABEL[status] ?? status}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
